import { useForm } from 'react-hook-form';
import { useMemo } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { IIncident } from 'interfaces/incident.interface';

/**
 * Custom hook to manage the incident form.
 *
 * This hook:
 * - Defines a validation schema for the form using Yup.
 * - Initializes the form with default values and validation rules.
 * - Returns the form instance for use in components.
 *
 * @returns {UseFormReturn<IIncident>} The form instance from `react-hook-form`.
 */
function useIncidentForm() {
  // Memoize the validation schema to avoid unnecessary re-renders.
  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        name: yup.string().required('This field is required!'), // Name is a required string field.
      }),
    [],
  );

  // Initialize the form with default values and the Yup validation resolver.
  const IncidentForm = useForm<IIncident>({
    defaultValues: {
      name: '', // Default value for the name field.
      description: '', // Default value for the description field.
      date: '', // Default value for the date field.
      archived: 0, // Default value for the archived field (0 = not archived).
    },
    resolver: yupResolver(validationSchema), // Use Yup for validation.
  });

  return IncidentForm; // Return the form instance.
}

export default useIncidentForm;
